import { flexyAlmacenes } from '../assets/data/arrays'
import { ROLE_ID_OPERACIONES } from '../constants/roleIds'
import {
  getFlexyAlmacenStock,
  getFlexyFulfillmentDisponibleStock,
  getFlexyWarehouseStock,
  getFlexyXeroxDisponibleStock,
  getProductEcommerceStock,
  getStockDisponibleSaldo,
  isFlexyStockFieldPresent,
  parseProductStockField
} from './productStockDisplayUtils'

/** Rol 16 — manager / operaciones Homecenter: BHOMEC en lugar de Xerox en lista de productos */
export function isManagerHomecenterProductListRole(
  roleId: number | undefined | null
): boolean {
  return roleId === ROLE_ID_OPERACIONES
}

export type ProductTableStockFilterKey =
  | 'fulfillment'
  | 'xerox'
  | 'exhibicion_xerox'
  | 'almacenamiento'
  | 'saldos'
  | 'reservas'
  | 'devoluciones'
  | 'bhomec'

export interface FlexyProductTableWarehouseConfig {
  filterKey: ProductTableStockFilterKey
  idalmacen: number
  codigo: string
  header: string
  key: string
  positiveClass: string
}

export interface ProductTableBodegaColumn {
  header: string
  filterKey: ProductTableStockFilterKey
  key: string
  positiveClass: string
}

/** Producto Flexy de GET /flexy/all (campos planos + flexy_almacenes). */
export type FlexyProductStockRecord = Record<string, unknown>

const flexyIdByCodigo = new Map(
  flexyAlmacenes.map((warehouse) => [warehouse.codigo, warehouse.idalmacen])
)

function flexyIdAlmacen(codigo: string): number {
  const idalmacen = flexyIdByCodigo.get(codigo)
  if (idalmacen == null) {
    throw new Error(`Almacén Flexy sin idalmacen: ${codigo}`)
  }
  return idalmacen
}

/** Almacenes Flexy en flexy_almacenes (GET /flexy/all). Los id salen de flexyAlmacenes. */
export const FLEXY_PRODUCT_TABLE_WAREHOUSES: readonly FlexyProductTableWarehouseConfig[] = [
  {
    filterKey: 'fulfillment',
    idalmacen: flexyIdAlmacen('FUL-MDM'),
    codigo: 'FUL-MDM',
    header: 'Fulfillment',
    key: 'fulfillment_stock_col',
    positiveClass: 'bg-blue-100 text-blue-800'
  },
  {
    filterKey: 'xerox',
    idalmacen: flexyIdAlmacen('XER-MDM'),
    codigo: 'XER-MDM',
    header: 'Xerox',
    key: 'xerox_stock_col',
    positiveClass: 'bg-teal-100 text-teal-800'
  },
  {
    filterKey: 'exhibicion_xerox',
    idalmacen: flexyIdAlmacen('EXXE-MDM'),
    codigo: 'EXXE-MDM',
    header: 'Exhibición',
    key: 'exhibicion_xerox_stock_col',
    positiveClass: 'bg-cyan-100 text-cyan-900'
  },
  {
    filterKey: 'almacenamiento',
    idalmacen: flexyIdAlmacen('ALM-MDM'),
    codigo: 'ALM-MDM',
    header: 'Almacenamiento',
    key: 'almacenamiento_stock_col',
    positiveClass: 'bg-purple-100 text-purple-800'
  },
  {
    filterKey: 'saldos',
    idalmacen: flexyIdAlmacen('SAL-MDM'),
    codigo: 'SAL-MDM',
    header: 'Saldos',
    key: 'saldos_stock_col',
    positiveClass: 'bg-amber-100 text-amber-900'
  },
  {
    filterKey: 'reservas',
    idalmacen: flexyIdAlmacen('RES-MDM'),
    codigo: 'RES-MDM',
    header: 'Reservas',
    key: 'reservas_stock_col',
    positiveClass: 'bg-indigo-100 text-indigo-900'
  },
  {
    filterKey: 'devoluciones',
    idalmacen: flexyIdAlmacen('DEV-MDM'),
    codigo: 'DEV-MDM',
    header: 'Devoluciones',
    key: 'devoluciones_stock_col',
    positiveClass: 'bg-orange-100 text-orange-900'
  }
] as const

const BHOMEC_WAREHOUSE: FlexyProductTableWarehouseConfig = {
  filterKey: 'bhomec',
  idalmacen: 0,
  codigo: 'BHOMEC',
  header: 'BHOMEC',
  key: 'bhomec_stock_col',
  positiveClass: 'bg-rose-100 text-rose-900'
}

const WAREHOUSE_CONFIG_BY_KEY = new Map(
  FLEXY_PRODUCT_TABLE_WAREHOUSES.map((warehouse) => [warehouse.filterKey, warehouse])
)

/** Bodegas visibles y en suma de stock por defecto (lista de productos). */
export const DEFAULT_PRODUCT_TABLE_BODEGAS_STANDARD: readonly ProductTableStockFilterKey[] = [
  'fulfillment',
  'xerox',
  'exhibicion_xerox',
  'reservas',
  'saldos'
] as const

export const DEFAULT_PRODUCT_TABLE_BODEGAS_HOMECENTER: readonly ProductTableStockFilterKey[] = [
  'fulfillment',
  'bhomec',
  'exhibicion_xerox',
  'reservas',
  'saldos'
] as const

function toBodegaColumn(
  warehouse: FlexyProductTableWarehouseConfig
): ProductTableBodegaColumn {
  return {
    header: warehouse.header,
    filterKey: warehouse.filterKey,
    key: warehouse.key,
    positiveClass: warehouse.positiveClass
  }
}

export function getProductTableBodegaColumns(
  roleId: number | undefined | null
): ProductTableBodegaColumn[] {
  const flexyColumns = FLEXY_PRODUCT_TABLE_WAREHOUSES.map(toBodegaColumn)
  if (isManagerHomecenterProductListRole(roleId)) {
    return [
      ...flexyColumns.filter((col) => col.filterKey !== 'xerox'),
      toBodegaColumn(BHOMEC_WAREHOUSE)
    ]
  }
  return flexyColumns
}

export function getDefaultSelectedBodegas(
  roleId: number | undefined | null
): ProductTableStockFilterKey[] {
  if (isManagerHomecenterProductListRole(roleId)) {
    return [...DEFAULT_PRODUCT_TABLE_BODEGAS_HOMECENTER]
  }
  return [...DEFAULT_PRODUCT_TABLE_BODEGAS_STANDARD]
}

export function getBodegaFilterOptions(roleId: number | undefined | null) {
  return getProductTableBodegaColumns(roleId).map(({ header, filterKey }) => ({
    label: header,
    value: filterKey
  }))
}

export function getValidBodegaFilterKeys(
  roleId: number | undefined | null
): Set<ProductTableStockFilterKey> {
  return new Set(getProductTableBodegaColumns(roleId).map((col) => col.filterKey))
}

export function isDefaultBodegasSelection(
  roleId: number | undefined | null,
  selected: readonly ProductTableStockFilterKey[]
): boolean {
  const defaults = getDefaultSelectedBodegas(roleId)
  return (
    selected.length === defaults.length &&
    defaults.every((field) => selected.includes(field))
  )
}

/** Mantiene solo claves válidas para el rol. */
export function sanitizeSelectedBodegasForRole(
  roleId: number | undefined | null,
  selected: readonly ProductTableStockFilterKey[]
): ProductTableStockFilterKey[] {
  const allowed = getValidBodegaFilterKeys(roleId)
  const isHomecenter = isManagerHomecenterProductListRole(roleId)
  const hasInvalid = selected.some((key) => !allowed.has(key))
  const hasRoleConflict =
    (isHomecenter && selected.includes('xerox')) ||
    (!isHomecenter && selected.includes('bhomec'))
  if (hasInvalid || hasRoleConflict) {
    return getDefaultSelectedBodegas(roleId)
  }
  const filtered = selected.filter((key) => allowed.has(key))
  return filtered.length > 0 ? [...filtered] : getDefaultSelectedBodegas(roleId)
}

function readFlexyFlatStockField(
  flexyProduct: FlexyProductStockRecord,
  field: string
): number | null {
  if (!isFlexyStockFieldPresent(flexyProduct[field])) return null
  return parseProductStockField(flexyProduct[field])
}

function getFlexyAlmacenStockForFilterKey(
  flexyProduct: FlexyProductStockRecord,
  filterKey: ProductTableStockFilterKey
): number | null {
  const config = WAREHOUSE_CONFIG_BY_KEY.get(filterKey)
  if (!config) return null
  return getFlexyAlmacenStock(flexyProduct, config.idalmacen)
}

export function getFlexyStockFilterValue(
  flexyProduct: FlexyProductStockRecord | undefined,
  filterKey: ProductTableStockFilterKey
): number | null {
  if (!flexyProduct) return null

  switch (filterKey) {
    case 'fulfillment': {
      const fromFlat = getFlexyFulfillmentDisponibleStock(flexyProduct)
      if (fromFlat != null) return fromFlat
      const fromAlmacen = getFlexyAlmacenStockForFilterKey(flexyProduct, 'fulfillment')
      if (fromAlmacen != null) return fromAlmacen
      const fromWarehouse = getFlexyWarehouseStock(flexyProduct, 'fulfillment')
      if (fromWarehouse != null) return fromWarehouse
      if (isFlexyStockFieldPresent(flexyProduct.stock_disponible)) {
        return getStockDisponibleSaldo(flexyProduct)
      }
      if (
        isFlexyStockFieldPresent(flexyProduct.stock_ecomerce) ||
        isFlexyStockFieldPresent(flexyProduct.stock_ecommerce)
      ) {
        return getProductEcommerceStock(flexyProduct)
      }
      return null
    }
    case 'xerox': {
      const fromFlat = getFlexyXeroxDisponibleStock(flexyProduct)
      if (fromFlat != null) return fromFlat
      const fromAlmacen = getFlexyAlmacenStockForFilterKey(flexyProduct, 'xerox')
      if (fromAlmacen != null) return fromAlmacen
      const fromWarehouse = getFlexyWarehouseStock(flexyProduct, 'xerox')
      if (fromWarehouse != null) return fromWarehouse
      return readFlexyFlatStockField(flexyProduct, 'stock_xerox')
    }
    case 'bhomec':
      return readFlexyFlatStockField(flexyProduct, 'stock_bhomec')
    default:
      return getFlexyAlmacenStockForFilterKey(flexyProduct, filterKey)
  }
}

function sumFlexyBodegasToStock(
  flexyProduct: FlexyProductStockRecord | undefined,
  stockFilterKeys: readonly ProductTableStockFilterKey[]
): number {
  if (!flexyProduct || stockFilterKeys.length === 0) return 0
  return stockFilterKeys.reduce((acc, filterKey) => {
    return acc + (getFlexyStockFilterValue(flexyProduct, filterKey) ?? 0)
  }, 0)
}

function getApiStockForBodegaFilter(
  product: Record<string, unknown>,
  filterKey: ProductTableStockFilterKey
): number {
  switch (filterKey) {
    case 'fulfillment':
      return getProductEcommerceStock(product)
    case 'xerox':
      return parseProductStockField(product.stock_xerox)
    case 'bhomec':
      return parseProductStockField(product.stock_bhomec)
    default:
      return 0
  }
}

function sumApiBodegasToStock(
  product: Record<string, unknown>,
  stockFilterKeys: readonly ProductTableStockFilterKey[]
): number {
  if (stockFilterKeys.length === 0) return 0
  return stockFilterKeys.reduce(
    (acc, filterKey) => acc + getApiStockForBodegaFilter(product, filterKey),
    0
  )
}

/**
 * Stock = solo bodegas seleccionadas en filtro.
 * Flexy: campos planos netos + flexy_almacenes | API SGL: fallback legacy.
 */
export function getStockDisplayValueForBodegas(
  flexyProduct: FlexyProductStockRecord | undefined,
  apiProduct: Record<string, unknown>,
  stockFilterKeys: readonly ProductTableStockFilterKey[],
  validKeys: Set<ProductTableStockFilterKey>
): number {
  const activeKeys = stockFilterKeys.filter((key) => validKeys.has(key))
  if (activeKeys.length === 0) return 0
  if (flexyProduct) {
    return sumFlexyBodegasToStock(flexyProduct, activeKeys)
  }
  return sumApiBodegasToStock(apiProduct, activeKeys)
}
